import { useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import { activeCurrencies } from "utils";
import BookCoverPreview from "components/BookCoverPreview";

const TableView = (props) => {
	const { books, filters } = props;
	const [expandedRows, setExpandedRows] = useState(null);

	const imgTemplate = (rowData) => {
		return (
			<BookCoverPreview
				coverUrl={rowData?.book_cover?.url}
				title={rowData?.title}
				thumbnailWidth={56}
				thumbnailHeight={70}
			/>
		);
	};

	const titleTemplate = (rowData) => {
		return (
			<div className="flex flex-col">
				<span className="font-semibold text-gray-900">{rowData?.title}</span>
				{rowData?.isbn && (
					<span className="text-xs text-gray-400">ISBN: {rowData?.isbn}</span>
				)}
			</div>
		);
	};

	const usdSalesTemplate = (rowData) => (
		<span className="font-semibold">${rowData?.sales?.usd}</span>
	);

	const zigSalesTemplate = (rowData) => (
		<span className="font-semibold">${rowData?.sales?.zwl}</span>
	);

	const copiesTemplate = (rowData) => (
		<span className="font-bold text-lg text-akgreener">
			{filters.revenueLine === "purchase"
				? rowData?.number_of_copies_purchased
				: rowData?.number_of_copies_subscribed}
		</span>
	);

	const categoryTemplate = (rowData) => (
		<Tag value={rowData?.category?.name || "N/A"} severity="info" className="text-xs" />
	);

	// Row expansion template
	const rowExpansionTemplate = (data) => {
		return (
			<div className="p-4 bg-gray-50 border-t border-gray-200">
				<Card className="shadow-none border-0">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
						{/* Book Details */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Book Details</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium text-gray-500">ISBN:</span>{" "}
									<span className="text-gray-800">{data?.isbn || "N/A"}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Author:</span>{" "}
									<span className="text-gray-800">{data?.author || "N/A"}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Category:</span>{" "}
									<span className="text-gray-800">{data?.category?.name || "N/A"}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Genre:</span>{" "}
									<span className="text-gray-800">{data?.genres?.[0]?.name || "N/A"}</span>
								</div>
							</div>
						</div>

						{/* Sales Details */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Sales Summary</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium text-gray-500">
										Copies {filters.revenueLine === "purchase" ? "Purchased" : "Subscribed"}:
									</span>{" "}
									<span className="text-akgreener font-bold text-lg">
										{filters.revenueLine === "purchase"
											? data?.number_of_copies_purchased
											: data?.number_of_copies_subscribed}
									</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Sales ({activeCurrencies.USD.label}):</span>{" "}
									<span className="text-gray-800 font-bold">${data?.sales?.usd || 0}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Sales ({activeCurrencies.ZWL.label}):</span>{" "}
									<span className="text-gray-800 font-bold">${data?.sales?.zwl || 0}</span>
								</div>
							</div>
						</div>

						{/* Pricing */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Pricing</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium text-gray-500">Price ({activeCurrencies.USD.label}):</span>{" "}
									<span className="text-gray-800">${data?.price || 0}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Price ({activeCurrencies.ZWL.label}):</span>{" "}
									<span className="text-gray-800">${data?.zwl_price || 0}</span>
								</div>
							</div>
						</div>
					</div>
				</Card>
			</div>
		);
	};

	return (
		<DataTable
			value={books}
			dataKey="id"
			tableClassName="font-sans text-sm"
			tableStyle={{ width: "100%" }}
			expandedRows={expandedRows}
			onRowToggle={(e) => setExpandedRows(e.data)}
			rowExpansionTemplate={rowExpansionTemplate}
			emptyMessage="No books found"
			responsiveLayout="scroll"
		>
			<Column
				expander
				style={{ width: "3rem" }}
			/>
			<Column
				header="Cover"
				body={imgTemplate}
				style={{ width: "70px" }}
				className="text-center"
			/>
			<Column
				header="Book Title"
				body={titleTemplate}
				style={{ minWidth: "200px" }}
			/>
			<Column
				header="Author(s)"
				field="author"
				style={{ minWidth: "150px" }}
			/>
			<Column
				header="Category"
				body={categoryTemplate}
				style={{ width: "130px" }}
			/>
			<Column
				header={`Copies ${
					filters.revenueLine === "purchase" ? "Purchased" : "Subscribed"
				}`}
				body={copiesTemplate}
				style={{ width: "130px" }}
				className="text-center"
			/>
			<Column
				header={`Sales (${activeCurrencies.USD.label})`}
				body={usdSalesTemplate}
				style={{ width: "120px" }}
			/>
			<Column
				header={`Sales (${activeCurrencies.ZWL.label})`}
				body={zigSalesTemplate}
				style={{ width: "120px" }}
			/>
		</DataTable>
	);
};

export default TableView;
